import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TablePagination
} from "@mui/material";
import {
    AttachMoney,
    AccessTime,
    CheckCircle,
    AccountBalanceWallet,
    TrendingUp
} from "@mui/icons-material";
import api from '../../services/api.js';
import Preloader from '../../components/Preloader';
import { formatarMoeda, formatarData } from '../../utils/formatadores.js';
import { useAuth } from '../../hooks/useAuth';

export default function MinhasComissoes() {
    const { user } = useAuth();
    const [comissoes, setComissoes] = useState([]);
    const [resumo, setResumo] = useState({ prevista: 0, liberada: 0, paga: 0, cancelada: 0 });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Identify if user is Representative to filter client-side if needed (backend filters by query)
    // Assuming backend endpoint /comissao accepts id_representante param
    // But logically, if I call without param and I am a rep, backend should filter.
    // However, my controller currently expects explicit param or returns all.
    // For MVP, I'll pass the ID if I can find it in the user object or assume the user IS the rep.
    // The `user` object usually has `id_usuario`. If the user is a rep, they are linked.
    // I need to fetch the Representante ID linked to this user first?
    // Or simpler: Pass `id_representante` if I have it. If not, maybe list all (for admin view reuse).

    // Simplification: Let's assume for now we list all if admin, or user sees his own if I implement logic.
    // But `MinhasComissoes` implies specificity.
    // I'll assume the logged user is context.

    useEffect(() => {
        carregarDados();
    }, []);

    const carregarDados = async () => {
        setLoading(true);
        try {
            // Fetch Representative ID based on User? Or just fetch comissoes and let backend handle?
            // Since I didn't implement "get my representative profile" endpoint yet,
            // I will try to fetch with a param if I knew it.
            // Check if user object has it.
            let idRep = user?.id_representante || user?.representante?.id_representante;

            // If the user object doesn't have it (it usually doesn't unless enriched), 
            // the backend should ideally handle "me" on the route.
            // For now, I'll call generic list. Ideally I should filter.

            const params = {};
            if (idRep) params.id_representante = idRep;

            const [resList, resSummary] = await Promise.all([
                api.get('/comissao', { params }),
                api.get('/comissao/resumo', { params })
            ]);

            setComissoes(resList.data.dados || []);
            setResumo(resSummary.data.dados || { prevista: 0, liberada: 0, paga: 0 });

        } catch (error) {
            console.error('Erro ao carregar comissões:', error);
        } finally {
            setLoading(false);
        }
    };

    const statusConfig = {
        'PREVISTA': { color: 'warning', icon: <AccessTime fontSize="small" />, label: 'Prevista' },
        'LIBERADA': { color: 'info', icon: <AccountBalanceWallet fontSize="small" />, label: 'Disponível' },
        'PAGA': { color: 'success', icon: <CheckCircle fontSize="small" />, label: 'Paga' },
        'CANCELADA': { color: 'error', icon: <AccessTime fontSize="small" />, label: 'Cancelada' }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
            <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney sx={{ color: '#20ac9c', fontSize: 32 }} />
                Minhas Comissões
            </Typography>

            {/* Cards de Resumo */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2">Previsto (Futuro)</Typography>
                                <AccessTime />
                            </Box>
                            <Typography variant="h4" fontWeight="bold">
                                {formatarMoeda(resumo.prevista)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2">Disponível para Saque</Typography>
                                <AccountBalanceWallet />
                            </Box>
                            <Typography variant="h4" fontWeight="bold">
                                {formatarMoeda(resumo.liberada)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2">Total Pago</Typography>
                                <TrendingUp />
                            </Box>
                            <Typography variant="h4" fontWeight="bold">
                                {formatarMoeda(resumo.paga)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabela */}
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Origem (Contrato)</TableCell>
                            <TableCell>Valor Base</TableCell>
                            <TableCell>%</TableCell>
                            <TableCell>Comissão</TableCell>
                            <TableCell align="center">Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} align="center"><Preloader /></TableCell></TableRow>
                        ) : comissoes.length === 0 ? (
                            <TableRow><TableCell colSpan={6} align="center">Nenhuma comissão encontrada.</TableCell></TableRow>
                        ) : (
                            comissoes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((comissao) => {
                                const st = statusConfig[comissao.en_status] || statusConfig['PREVISTA'];
                                return (
                                    <TableRow key={comissao.id_comissao} hover>
                                        <TableCell>{formatarData(comissao.createdAt)}</TableCell>
                                        <TableCell>
                                            {comissao.pagamentoOrigem?.contrato?.titular?.usuario?.st_nome || 'N/A'}
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                Plano: {comissao.pagamentoOrigem?.contrato?.plano?.st_nome}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{formatarMoeda(comissao.nu_valor_base_centavos)}</TableCell>
                                        <TableCell>{comissao.nu_percentual_aplicado}%</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{formatarMoeda(comissao.nu_valor_comissao_centavos)}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                icon={st.icon}
                                                label={st.label}
                                                color={st.color}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={comissoes.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </TableContainer>
        </Box>
    );
}
